import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Tooltip, Typography } from "@mui/material";
import ActionEvidenceList from "features/programmesActions/ActionEvidenceList";
import ActionEvidenceUpload from "features/programmesActions/ActionEvidenceUpload";
import { getFormattedCategoryName } from "features/programmesActions/ActionList";
import UnifiedEvidenceList from "features/programmesActions/UnifiedEvidenceList";
import { getEvidenceType } from "model";
import type { FarmerActionData } from "../hooks/useInsightsDashboardQueries";

interface Props {
  action: FarmerActionData;
  farmerGuid?: string;
}

export default function ActionDetailView({ action, farmerGuid }: Props) {
  const categoryId = parseInt(action["SubmittedActions.categoryId"] ?? "0", 10);
  const category =
    getFormattedCategoryName(categoryId) ||
    action["SubmittedActions.category"] ||
    "N/A";
  const activity = action["SubmittedActions.action"] || "N/A";
  const submittedDetails = action["SubmittedActions.submittedDetails"];
  const detailQuestion = action["SubmittedActions.detailQuestion"];
  const actionId = action["SubmittedActions.actionId"] || "";
  const guid = action["SubmittedActions.guid"];
  const requiredEvidence = action["SubmittedActions.requiredEvidence"] || "";
  const evidenceType = getEvidenceType(requiredEvidence);
  const showUnifiedEvidence = farmerGuid && evidenceType;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
      }}
    >
      {/* Left Section - Action Details */}
      <Box
        sx={{
          flex: { xs: "1 1 auto", md: "0 0 379px" },
          border: "1px solid #e4e4e7",
          borderRadius: "8px",
          p: 3,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: "24px",
              fontWeight: 600,
              mb: 0.75,
              color: "#09090b",
            }}
          >
            Action Details
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#71717a", fontSize: "14px" }}
          >
            Details about the action completed by the farmer
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Category */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 1,
                color: "#18181b",
                fontSize: "14px",
              }}
            >
              Category
            </Typography>
            <Box
              sx={{
                bgcolor: "#eaeaea",
                border: "1px solid #e4e4e7",
                borderRadius: "6px",
                minHeight: "40px",
                display: "flex",
                alignItems: "center",
                px: 1.5,
                py: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "#18181b",
                  fontSize: "14px",
                }}
              >
                {category}
              </Typography>
            </Box>
          </Box>

          {/* Activity */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 1,
                color: "#18181b",
                fontSize: "14px",
              }}
            >
              Activity
            </Typography>
            <Box
              sx={{
                bgcolor: "#eaeaea",
                border: "1px solid #e4e4e7",
                borderRadius: "6px",
                minHeight: "80px",
                p: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#18181b",
                  fontSize: "14px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {activity}
              </Typography>
            </Box>
          </Box>

          {/* Farmer's Notes */}
          {submittedDetails && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "#18181b",
                    fontSize: "14px",
                  }}
                >
                  Farmer&apos;s notes
                </Typography>
                {detailQuestion && (
                  <Tooltip
                    title={
                      <Typography component="span">
                        This is the farmer&apos;s answer to the question:{" "}
                        <Typography
                          component="span"
                          sx={{ fontStyle: "italic", fontSize: "14px" }}
                        >
                          {detailQuestion}
                        </Typography>
                      </Typography>
                    }
                    arrow
                  >
                    <InfoIcon
                      sx={{
                        fontSize: "16px",
                        color: "#71717a",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Box
                sx={{
                  bgcolor: "#eaeaea",
                  border: "1px solid #e4e4e7",
                  borderRadius: "6px",
                  minHeight: "80px",
                  p: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#18181b",
                    fontSize: "14px",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {submittedDetails}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Section - Documentation */}
      <Box
        sx={{
          flex: "1 1 auto",
          border: "1px solid #e4e4e7",
          borderRadius: "8px",
          p: 3,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AssignmentTurnedInIcon sx={{ color: "#16a34a", fontSize: 28 }} />
            <Typography
              variant="h4"
              sx={{
                fontSize: "24px",
                fontWeight: 600,
                mb: 0.75,
                color: "#09090b",
              }}
            >
              Documentation
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "#71717a", fontSize: "14px", mt: 1 }}
          >
            Upload photos and other evidence for this action.
          </Typography>
        </Box>
        {guid && farmerGuid && showUnifiedEvidence ? (
          <UnifiedEvidenceList
            evidenceType={evidenceType}
            actionSubmissionGuid={guid}
            requiredEvidence={requiredEvidence}
            userGuid={farmerGuid}
            readOnly
          />
        ) : (
          guid && (
            <>
              <Box sx={{ mb: 2 }}>
                <ActionEvidenceUpload
                  actionSubmissionGuid={guid}
                  requiredEvidence={requiredEvidence}
                />
              </Box>
              <ActionEvidenceList actionSubmissionGuid={guid} />
            </>
          )
        )}
      </Box>
    </Box>
  );
}
